import React, {useEffect, useState} from 'react'
import { Link, useParams } from 'react-router-dom';
import { FaWhatsapp, FaTelegramPlane, FaMobileAlt, FaEnvelope } from 'react-icons/fa';

import './Model.css';

import Header from '../../sections/Header/Header';
import Footer from '../../sections/Footer/Footer';
import SEO from '../../components/SEO/SEO';
import Dropdown from '../../components/Dropdown/Dropdown';
import Table from '../../components/Table/Table';
import Loader from '../../components/Loader/Loader';
import PreviewSlider from '../../components/Slider/PhotoSlider/PhotoSlider';
import MoreTalent from '../Talent/components/MoreTalent';

import { getPublicModel, getPublicModels, modelProfilePath } from '../../../services/modelService';
import { getModelPhoto, getModelPhotos, hasModelPhoto } from '../../../util/ModelPhotos';

const titleCase = (value = '') => String(value).charAt(0).toUpperCase() + String(value).slice(1);
const normalizedSlug = (value = '') => String(value).trim().toLowerCase();
const displayValue = (value) => value === null || value === undefined || value === '' ? 'N/A' : value;
const formatRate = (value) => typeof value === 'number' ? `${value}€` : displayValue(value);
const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
};

const contactHref = (key, value) => {
    if (!value) return '';
    if (key === 'whatsapp') return `https://wa.me/${String(value).replace(/[^0-9]/g, '')}`;
    if (key === 'telegram') return `https://t.me/${String(value).replace(/^@/, '')}`;
    if (key === 'phone') return `tel:${value}`;
    if (key === 'email') return `mailto:${value}`;
    return String(value);
};

const videoSource = (video) => typeof video === 'string' ? video : video?.url || video?.videoUrl || video?.src || '';

const contactIcons = {
    whatsapp: FaWhatsapp,
    telegram: FaTelegramPlane,
    phone: FaMobileAlt,
    email: FaEnvelope
};

const Model = () => {
    const {city, slug} = useParams();
    const [profile, setProfile] = useState(null);
    const [relatedModels, setRelatedModels] = useState([]);
    const [videoModalIndex, setVideoModalIndex] = useState(null);
    const [photoModalIndex, setPhotoModalIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileError, setProfileError] = useState('');

    useEffect(() => {
        let mounted = true;
        setProfile(null);
        setRelatedModels([]);
        setVideoModalIndex(null);
        setPhotoModalIndex(null);
        setLoading(true);
        setProfileError('');

        getPublicModel(city, slug)
            .then((modelProfile) => { if (mounted) setProfile(modelProfile); })
            .catch((loadError) => { if (mounted) setProfileError(loadError.message || 'We could not load this model profile.'); })
            .finally(() => { if (mounted) setLoading(false); });

        getPublicModels(city)
            .then((models) => { if (mounted) setRelatedModels(models); })
            .catch(() => {
                // Related navigation is optional; keep the profile available if the catalogue request fails.
            });

        return () => { mounted = false; };
    }, [city, slug]);

    useEffect(() => {
        if (videoModalIndex === null && photoModalIndex === null) return undefined;
        const videoCount = Array.isArray(profile?.videos) ? profile.videos.map(videoSource).filter(Boolean).length : 0;
        const photoCount = profile ? getModelPhotos(profile).length : 0;
        const handleMediaModalKeyDown = (event) => {
            if (event.key === 'Escape') {
                setVideoModalIndex(null);
                setPhotoModalIndex(null);
                return;
            }
            const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
            if (!offset) return;
            event.preventDefault();
            if (videoModalIndex !== null && videoCount > 1) {
                setVideoModalIndex((current) => current === null ? current : (current + offset + videoCount) % videoCount);
            }
            if (photoModalIndex !== null && photoCount > 1) {
                setPhotoModalIndex((current) => current === null ? current : (current + offset + photoCount) % photoCount);
            }
        };
        window.addEventListener('keydown', handleMediaModalKeyDown);
        return () => window.removeEventListener('keydown', handleMediaModalKeyDown);
    }, [profile, videoModalIndex, photoModalIndex]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [city, slug]);

    const routeName = titleCase(slug || 'model');
    const routeCity = titleCase(city || 'Italy');

    if (!loading && !profile) {
        return (
            <>
                <SEO title={`${routeName} • Escort ${routeCity}`} description={`The requested model profile is not available in ${routeCity}.`} />
                <Header whitebar={true} />
                <section className="model model-state">
                    <div className="model-state-content">
                        <p className="model-profile-error" role="alert">{profileError || 'Model not found.'}</p>
                        <Link to={`/talent/${city || ''}`}>View available talent</Link>
                    </div>
                </section>
                <Footer />
            </>
        );
    }

    if (loading) {
        return (
            <>
                <SEO title={`${routeName} • Escort ${routeCity}`} description={`Loading the ${routeName} model profile in ${routeCity}.`} />
                <Header whitebar={true} />
                <section className="model model-state">
                    <div className="model-state-content">
                        <Loader className="model-loader" />
                    </div>
                </section>
                <Footer />
            </>
        );
    }

    const profileName = profile.name || routeName;
    const profileCity = profile.city || routeCity;
    const profileCitySlug = profile.citySlug || city;
    const profilePhotos = getModelPhotos(profile);
    const hasProfilePhotos = hasModelPhoto(profile);
    const galleryPhotos = profilePhotos;
    const modelVideos = Array.isArray(profile.videos) ? profile.videos.map(videoSource).filter(Boolean) : [];
    const parameters = profile.parameters || {};
    const cityRates = Array.isArray(profile.cityRates) ? profile.cityRates : [];
    const travelRates = Array.isArray(profile.travelRates) ? profile.travelRates : [];
    const tourSchedule = Array.isArray(profile.tourSchedule) ? profile.tourSchedule : (Array.isArray(profile.tourDates) ? profile.tourDates : []);
    const contacts = profile.contacts || {};
    const modelIndex = relatedModels.findIndex((item) => normalizedSlug(item.slug || item.name) === normalizedSlug(profile.slug || profileName));
    const hasRelatedModels = relatedModels.length > 1 && modelIndex >= 0;
    const previousModel = hasRelatedModels ? relatedModels[(modelIndex - 1 + relatedModels.length) % relatedModels.length] : null;
    const nextModel = hasRelatedModels ? relatedModels[(modelIndex + 1) % relatedModels.length] : null;
    const parameterData = [{
        height: displayValue(parameters.height),
        weight: displayValue(parameters.weight),
        body: displayValue(parameters.body),
        hair: displayValue(parameters.hair),
        eyes: displayValue(parameters.eyes),
        breast: displayValue(parameters.breast)
    }];
    const rateData = (rates) => rates.length ? rates.map((rate) => ({
        duration: displayValue(rate.duration),
        incall: formatRate(rate.incall),
        outcall: formatRate(rate.outcall)
    })) : [{duration: 'N/A', incall: 'N/A', outcall: 'N/A'}];
    const tourData = tourSchedule.map((tour) => ({
        city: displayValue(tour.city),
        range: [formatDate(tour.from), formatDate(tour.to)].filter(Boolean).join(' – ') || displayValue(tour.range)
    }));
    const contactLinks = ['whatsapp', 'telegram', 'phone', 'email']
        .filter((key) => contacts[key])
        .map((key) => ({key, Icon: contactIcons[key], href: contactHref(key, contacts[key])}));

    return (
        <>
            <SEO title={`${profileName} • Escort ${profileCity}`} description={`${profileName} is available in ${profileCity}. Book through SADIDA Network, Italy's premier adult entertainment agency.`} />
            <Header whitebar={true} />
            <section className="model">
                <div className="model-view left">
                    <div className="model-view-desktop">
                        {galleryPhotos.map((photo, index) => (
                            <div className={hasProfilePhotos ? 'model-photo' : 'model-photo model-photo-fallback'} key={`${photo}-${index}`}>
                                <img src={photo} alt={hasProfilePhotos ? `${profileName} in ${profileCity}` : `${profileName} profile avatar`} />
                            </div>
                        ))}
                        {hasRelatedModels && <MoreTalent
                            prev={{
                                name: previousModel.name,
                                link: modelProfilePath(previousModel.citySlug || profileCitySlug, previousModel),
                                city: previousModel.city || profileCity,
                                photo: getModelPhoto(previousModel)
                            }}
                            next={{
                                name: nextModel.name,
                                link: modelProfilePath(nextModel.citySlug || profileCitySlug, nextModel),
                                city: nextModel.city || profileCity,
                                photo: getModelPhoto(nextModel)
                            }}
                        />}
                        <Footer type={'side'} />
                    </div>
                    <div className="model-view-mobile">
                        <PreviewSlider col={1} images={galleryPhotos} onImageClick={setPhotoModalIndex} />
                    </div>
                </div>
                <div className="model-view right">
                    <div className="right-container scrollable">
                        {modelVideos.length > 0 && <div className="model-video-teaser">
                            <button type="button" className="model-video-teaser-play" onClick={() => setVideoModalIndex(0)} aria-label={`Play ${profileName} video`}>
                                <video src={modelVideos[0]} autoPlay muted loop playsInline preload="metadata" aria-hidden="true" />
                                <span className="model-video-teaser-label">Play video</span>
                            </button>
                        </div>}
                        <div className="model-name">{profileName}</div>
                        <div className="model-city">{profileCity}</div>
                        <Dropdown
                            title={'Description'}
                            defaultStatus={true}
                            pad={true}
                            content={
                                <>
                                    <p>{profile.description || 'Profile details are currently unavailable.'}</p>
                                    {contactLinks.length > 0 && <div className="model-contacts">
                                        <div className="model-contacts-title">CONTACT {profileName.toUpperCase()}</div>
                                        {contactLinks.map(({key, Icon, href}) => (
                                            <a href={href} key={key} target={key === 'whatsapp' || key === 'telegram' ? '_blank' : undefined} rel={key === 'whatsapp' || key === 'telegram' ? 'noopener noreferrer' : undefined} className="model-contact-icon" aria-label={`${key} ${profileName}`}>
                                                <Icon />
                                            </a>
                                        ))}
                                    </div>}
                                </>
                            }
                        />
                        <Dropdown
                            title={'Model Parameters'}
                            content={<Table columns={[
                                {id: 'height', label: 'Height', class: 'height-col'},
                                {id: 'weight', label: 'Weight', class: 'weight-col'},
                                {id: 'body', label: 'Body', class: 'body-col'},
                                {id: 'hair', label: 'Hair', class: 'hair-col'},
                                {id: 'eyes', label: 'Eyes', class: 'eyes-col'},
                                {id: 'breast', label: 'Breast', class: 'breast-col'}
                            ]} data={parameterData} />}
                        />
                        <Dropdown
                            title={'City Rates'}
                            content={<Table columns={[
                                {id: 'duration', label: 'Duration', class: 'duration-col'},
                                {id: 'incall', label: 'Incall', class: 'incall-col'},
                                {id: 'outcall', label: 'Outcall', class: 'outcall-col'}
                            ]} data={rateData(cityRates)} />}
                        />
                        <Dropdown
                            title={'Travel Rates'}
                            content={<Table columns={[
                                {id: 'duration', label: 'Duration', class: 'duration-col'},
                                {id: 'incall', label: 'Incall', class: 'incall-col'},
                                {id: 'outcall', label: 'Outcall', class: 'outcall-col'}
                            ]} data={rateData(travelRates)} />}
                        />
                        {tourData.length > 0 && <Dropdown
                            title={'Tour Dates'}
                            content={<div className="tour-dates-list">
                                {tourData.map((tour, index) => (
                                    <div className="tour-date-item" key={`${tour.city}-${index}`}>
                                        <span className="tour-date-city">{tour.city}</span>
                                        <span className="tour-date-range">{tour.range}</span>
                                    </div>
                                ))}
                            </div>}
                        />}
                        <div className="model-fantasy-cta">
                            <Link to={`/fantasy/create?model=${encodeURIComponent(profile.slug || slug)}`}>Create a custom fantasy with {profileName}</Link>
                        </div>
                    </div>
                </div>
                <div className="model-view-mobile">
                    {hasRelatedModels && <MoreTalent
                        prev={{
                            name: previousModel.name,
                            link: modelProfilePath(previousModel.citySlug || profileCitySlug, previousModel),
                            city: previousModel.city || profileCity,
                            photo: getModelPhoto(previousModel)
                        }}
                        next={{
                            name: nextModel.name,
                            link: modelProfilePath(nextModel.citySlug || profileCitySlug, nextModel),
                            city: nextModel.city || profileCity,
                            photo: getModelPhoto(nextModel)
                        }}
                    />}
                    <Footer />
                </div>
                {videoModalIndex !== null && modelVideos.length > 0 && <div className="model-video-modal" onClick={() => setVideoModalIndex(null)} role="presentation">
                    <div className="model-video-modal-panel" role="dialog" aria-modal="true" aria-label="Model video viewer" onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="model-video-modal-close" onClick={() => setVideoModalIndex(null)} aria-label="Close video viewer">&times;</button>
                        <video key={modelVideos[videoModalIndex]} className="model-video-modal-player" src={modelVideos[videoModalIndex]} controls autoPlay playsInline />
                        <span className="model-video-modal-count">{videoModalIndex + 1} / {modelVideos.length}</span>
                        {modelVideos.length > 1 && <button type="button" className="model-video-modal-next" onClick={() => setVideoModalIndex((videoModalIndex + 1) % modelVideos.length)} aria-label="Next video">→</button>}
                    </div>
                </div>}
                {photoModalIndex !== null && galleryPhotos.length > 0 && <div className="model-photo-modal" onClick={() => setPhotoModalIndex(null)} role="presentation">
                    <div className="model-photo-modal-panel" role="dialog" aria-modal="true" aria-label="Model photo viewer" onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="model-photo-modal-close" onClick={() => setPhotoModalIndex(null)} aria-label="Close photo viewer">&times;</button>
                        {galleryPhotos.length > 1 && <button type="button" className="model-photo-modal-prev" onClick={() => setPhotoModalIndex((photoModalIndex - 1 + galleryPhotos.length) % galleryPhotos.length)} aria-label="Previous photo">←</button>}
                        <img key={galleryPhotos[photoModalIndex]} className="model-photo-modal-image" src={galleryPhotos[photoModalIndex]} alt={`${profileName} ${photoModalIndex + 1}`} />
                        {galleryPhotos.length > 1 && <button type="button" className="model-photo-modal-next" onClick={() => setPhotoModalIndex((photoModalIndex + 1) % galleryPhotos.length)} aria-label="Next photo">→</button>}
                        <span className="model-photo-modal-count">{photoModalIndex + 1} / {galleryPhotos.length}</span>
                    </div>
                </div>}
            </section>
        </>
    )
}

export default Model;
